/**
 * Module dependencies.
 */

import React, { useMemo } from 'react';
import { Controller, FieldValues, UseFormReturn } from 'react-hook-form';
import { PhoneNumber } from 'app/components/atoms/forms/fields/phone-number/phone-number';
import { PhoneNumberFormItem } from '../form-types';
import { useGetDialCodes } from 'app/hooks/app-requests/use-get-dial-codes';
import { parsePhoneNumber as parsePhoneLib, validatePhoneNumber } from 'app/core/utils/phone-utils';

/**
 * `FormPhoneNumberFieldProps` type.
 */

export type FormPhoneNumberFieldProps = {
  name: string;
  item: PhoneNumberFormItem;
  form: UseFormReturn<FieldValues, any>;
};

/**
 * Default parse value function.
 */

function defaultParseValue(value: string) {
  return parsePhoneLib(value);
}

/**
 * Export `FormPhoneNumberField` component.
 */

export function FormPhoneNumberField(props: FormPhoneNumberFieldProps): JSX.Element {
  const { form, item } = props;
  const { control, watch } = form;
  const { name, label, placeholder, disabled, defaultDialCode, rules, parseValue = defaultParseValue, onChange } = item;

  const values = watch();

  // Load dial codes
  const { data: dialCodes = [], isLoading } = useGetDialCodes();

  // Compute disabled state
  const isDisabled = useMemo(() => {
    if (typeof disabled === 'function') {
      return disabled(values, null);
    }

    return disabled || isLoading;
  }, [disabled, values, isLoading]);

  // Validation rules
  const validationRules = useMemo(() => {
    const baseRules = { ...rules };

    // Add phone number validation if not already present
    if (baseRules && !baseRules.validate?.validPhoneNumber) {
      baseRules.validate = {
        ...baseRules.validate,
        validPhoneNumber: (value: string) => {
          if (!value) {
            // If empty, let 'required' rule handle it
            return true;
          }

          const parsed = parseValue(value);

          if (!parsed) {
            return 'Invalid phone number format';
          }

          // Find country code from dial code
          const dialCode = dialCodes.find(dc => dc.dialCode === parsed.dialCode);
          const isValid = validatePhoneNumber(value, dialCode?.code);

          return isValid || 'Invalid phone number for selected country';
        }
      };
    }

    return baseRules;
  }, [rules, dialCodes, parseValue]);

  return (
    <Controller
      control={control}
      name={name}
      render={({ field, fieldState: { error, invalid, isTouched } }) => (
        <PhoneNumber
          defaultDialCode={defaultDialCode}
          dialCodes={dialCodes}
          disabled={isDisabled}
          error={error}
          invalid={invalid}
          isTouched={isTouched}
          label={label}
          name={name}
          onChange={value => {
            field.onChange(value);

            if (onChange) {
              onChange(value, form);
            }
          }}
          placeholder={placeholder ?? placeholder === false ? undefined : label}
          value={field.value}
        />
      )}
      rules={validationRules}
    />
  );
}
